import { J1BacktomenuarrowB, SidebarIcon } from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'
import { IconButton, PaperProps, useMediaQuery, useTheme } from '@mui/material'
import Drawer, { DrawerProps } from '@mui/material/Drawer'
import List from '@mui/material/List'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FullLogoWrapper } from 'src/Components/app-logos'
import { useHasNewFeature } from 'src/Libs/new-content'
import { makeStyles } from 'tss-react/mui'
import Categories, { Category, CategoryItem } from './Categories'
import { CategoryListItem } from './CategoryItemList'

interface StyleProps {
  back?: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, { back }) => ({
  logoWrapper: {
    marginRight: 'auto',
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(6),
  },
  logo: {
    padding: theme.spacing(2),
    maxHeight: 100,
    minHeight: 100,
    width: 153,
  },
  drawer: {
    width: theme.shape.sidebar.drawerWidth,
    minWidth: theme.shape.sidebar.drawerCollapsedWidth,
    overflow: 'hidden',
  },
  drawerCollapsed: {
    width: theme.shape.sidebar.drawerCollapsedWidth,
  },
  paper: {
    width: theme.shape.sidebar.drawerWidth,
    alignItems: 'center',
    justifyContent: 'space-between',
    background: theme.palette.background.sidebar,
    overflowX: 'hidden',
    zIndex: 1201,
    paddingBottom: 0,

    borderRadius: theme.shape.sidebar.borderRadius,
    height: theme.shape.sidebar.height,
    top: theme.shape.sidebar.top,
    bottom: theme.shape.sidebar.bottom,
    left: theme.shape.sidebar.left,
  },
  spacingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100vh',
  },
  versionWrapper: {
    height: theme.spacing(4),
    position: 'absolute',
    bottom: 0,
    marginBottom: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.primary.main,
  },
  firstDrawer: {
    position: 'absolute',
    width: theme.shape.sidebar.drawerWidth,
  },
  secondDrawer: {
    left: theme.shape.sidebar.drawerWidth,
    width: theme.shape.sidebar.drawerWidth,
    position: 'absolute',
    height: '100%',
  },
  wrapper: {
    height: '100%',
    position: 'relative',
    width: theme.shape.sidebar.drawerWidth,
    overflowY: 'auto',
    overflowX: 'hidden',
    colorScheme: 'dark',
    scrollbarColor: `${theme.palette.grey.grey00} transparent`,

    '&::-webkit-scrollbar': {
      width: 12,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 8,
      background: theme.palette.grey.grey00,
    },
  },
  logoCollapsed: {
    width: 0,
  },
  collapseTransition: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  },
  translate: {
    transform: `translateX(${back === true ? '-' : '+'}${back ? theme.shape.sidebar.drawerWidth : 0}px)`,
    transition: '0.3s ease-in-out',
  },
  collapseIcon: {
    color: theme.palette.text.sidebar,
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(3),
    padding: 0,

    '& > svg': {
      fontSize: '30px',
    },
  },
}))

export interface SidebarElementProps {
  links: Category[]
  onClose?: () => void
  PaperProps?: Partial<PaperProps>
  open: boolean
  variant?: DrawerProps['variant']
  anchor?: DrawerProps['anchor']
  privacyLinks?: React.FC<unknown>
  collapsible?: boolean
  collapsedListClass?: string
  listClasses?: string
  categoriesClasses?: string
  categoryItemClasses?: string
  showActiveIndicator?: boolean
  logoComponent?: React.ReactElement
  children?: React.ReactNode
}

/**
 * Sidebar element that contains the menu of the app
 */
const SidebarElement: React.FC<SidebarElementProps> = ({
  links,
  onClose,
  PaperProps,
  open,
  children,
  variant = 'permanent',
  anchor,
  privacyLinks: PrivacyLinks,
  collapsible, // if true, sidebar can be collapsed to a mini version only displaying icons
  collapsedListClass,
  listClasses,
  categoriesClasses,
  categoryItemClasses,
  showActiveIndicator,
  logoComponent,
}) => {
  const { t } = useCustomTranslation()
  const [openSecond, setOpenSecond] = useState<boolean>(false)
  const [collapsed, setCollapsed] = useState<boolean>(false)
  const { classes, cx } = useStyles({ back: openSecond })
  const [currentLink, setCurrentLink] = useState<CategoryItem | undefined>()
  const myRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { handleUpdateContentVersion } = useHasNewFeature()

  useEffect(() => {
    if (currentLink) {
      myRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setOpenSecond(true)
    }
  }, [currentLink])

  useEffect(() => {
    if (!openSecond && currentLink) {
      setTimeout(() => setCurrentLink(undefined), 1000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSecond])

  const onSidebarCollapse = (isCollapsed: boolean): void => {
    setCollapsed(!isCollapsed)
    dispatch(appUpdate({ sidebarCollapsed: !isCollapsed }))
  }

  const handleCategoryListItemClick = (item: CategoryItem, items?: Category[]): void => {
    if (item.hasNewContent && item.elementKeys) handleUpdateContentVersion(item.elementKeys)
    if (items) setCurrentLink(item)
  }

  return (
    <Drawer
      variant={variant}
      anchor={anchor}
      PaperProps={PaperProps}
      open={open}
      onClose={onClose}
      className={cx(classes.drawer, classes.collapseTransition, { [classes.drawerCollapsed]: collapsed })}
      classes={{ paper: cx(classes.paper, classes.collapseTransition, { [classes.drawerCollapsed]: collapsed }) }}
    >
      <div className={classes.spacingContainer}>
        {collapsible && (
          <IconButton className={classes.collapseIcon} onClick={() => onSidebarCollapse(collapsed)}>
            <SidebarIcon />
          </IconButton>
        )}
        {logoComponent ?? (
          <FullLogoWrapper
            transitionStyle={cx(classes.logoWrapper, classes.collapseTransition, { [classes.logoCollapsed]: collapsed })}
            containerStyle={cx(classes.logoWrapper, classes.logo, classes.collapseTransition, { [classes.logoCollapsed]: collapsed })}
          />
        )}

        {links && links.length > 0 && (
          <div className={cx(classes.wrapper, { [classes.drawerCollapsed]: collapsed, [classes.collapseTransition]: collapsed })}>
            <div className={cx(classes.firstDrawer, openSecond !== undefined ? classes.translate : undefined)} ref={myRef}>
              <List className={cx(listClasses)}>
                <Categories
                  categories={links}
                  onClose={onClose}
                  handleCategoryListItemClick={handleCategoryListItemClick}
                  collapsed={collapsed}
                  collapsedListClass={collapsedListClass}
                  categoriesClasses={categoriesClasses}
                  categoryItemClasses={categoryItemClasses}
                  showActiveIndicator={showActiveIndicator}
                />
              </List>
            </div>
            {currentLink && (
              <div className={cx(classes.secondDrawer, openSecond !== undefined ? classes.translate : undefined)}>
                <CategoryListItem
                  onClick={() => {
                    setOpenSecond(false)
                    navigate('/')
                  }}
                  icon={<J1BacktomenuarrowB />}
                  translationKey={t('common.back')}
                  dataQa="framework-back"
                  collapsed={collapsed}
                  showActiveIndicator={showActiveIndicator}
                />
                <List className={listClasses}>
                  <Categories
                    categories={currentLink.items}
                    categoriesClasses={categoriesClasses}
                    categoryItemClasses={categoryItemClasses}
                    onClose={onClose}
                    handleCategoryListItemClick={handleCategoryListItemClick}
                    collapsed={collapsed}
                    collapsedListClass={collapsedListClass}
                    showActiveIndicator={showActiveIndicator}
                  />
                </List>
              </div>
            )}
          </div>
        )}
        {!collapsed && children}
        {isMobile && PrivacyLinks && (
          <div className={classes.versionWrapper}>
            <PrivacyLinks />
          </div>
        )}
      </div>
    </Drawer>
  )
}

export default SidebarElement
